<template>
  <div>
    <div
      class="d-flex justify-content-between align-items-center mb-3 flex-wrap"
    >
      <a-button type="primary" icon="plus" @click="openModal(null)">
        {{ $t("action.ajouter") }}
      </a-button>
    </div>
    <a-table
      row-key="_id"
      :loading="loading"
      :columns="columns"
      :data-source="rows"
      :pagination="{ pageSize: 10 }"
      :scroll="{ x: 'max-content' }"
    >
      <template slot="expenseKind" slot-scope="text">
        <a-tag :color="kindColor(text)">{{ kindLabel(text) }}</a-tag>
      </template>
      <template slot="action" slot-scope="text, record">
        <div class="editable-row-operations">
          <a-button type="primary" size="small" @click="openModal(record)">
            {{ $t("action.modifier") }}
          </a-button>
          <a-button type="danger" size="small" class="ml-2">
            <a-popconfirm
              :title="$t('comptabilite.depenseTypeDeactivateConfirm')"
              @confirm="() => remove(record._id)"
            >
              <a>{{ $t("action.supprimer") }}</a>
            </a-popconfirm>
          </a-button>
        </div>
      </template>
    </a-table>

    <a-modal
      :visible="modalVisible"
      :title="editingId ? $t('action.modifier') : $t('action.ajouter')"
      :confirm-loading="saving"
      :width="720"
      @ok="onModalOk"
      @cancel="closeModal"
      destroy-on-close
      wrap-class-name="parametrage-compact-modal"
    >
      <a-form :form="form" layout="vertical" class="compact-param-form">
        <div class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('etablissement.nom')">
              <a-input
                v-decorator="[
                  'name',
                  {
                    rules: [{ required: true, message: $t('requis.nom') }],
                  },
                ]"
              />
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.expenseKind')">
              <a-select
                v-decorator="[
                  'expenseKind',
                  { initialValue: 'OPEX', rules: [{ required: true }] },
                ]"
                @change="onKindChange"
              >
                <a-select-option value="OPEX">{{
                  $t("comptabilite.expenseKindOpex")
                }}</a-select-option>
                <a-select-option value="CAPEX">{{
                  $t("comptabilite.expenseKindCapex")
                }}</a-select-option>
                <a-select-option value="OTHER">{{
                  $t("comptabilite.expenseKindOther")
                }}</a-select-option>
              </a-select>
            </a-form-item>
          </div>
        </div>
        <div v-if="showCapexFormula" class="row">
          <div class="col-12">
            <a-form-item :label="$t('comptabilite.capexFormula')">
              <a-textarea
                v-decorator="['capexFormula', { initialValue: '' }]"
                :rows="2"
                :placeholder="$t('comptabilite.capexFormula')"
              />
            </a-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <a-form-item :label="$t('comptabilite.description')">
              <a-textarea
                v-decorator="['description', { initialValue: '' }]"
                :rows="2"
                :placeholder="$t('comptabilite.description')"
              />
            </a-form-item>
          </div>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import { showApiError } from "@/utilities/showApiError";

export default {
  name: "TypesTab",
  beforeMount() {
    this.form = this.$form.createForm(this);
  },
  data() {
    return {
      loading: false,
      rows: [],
      modalVisible: false,
      saving: false,
      editingId: null,
      showCapexFormula: false,
    };
  },
  computed: {
    columns() {
      return [
        {
          title: this.$t("etablissement.nom"),
          dataIndex: "name",
          key: "name",
        },
        {
          title: this.$t("comptabilite.expenseKind"),
          dataIndex: "expenseKind",
          key: "expenseKind",
          scopedSlots: { customRender: "expenseKind" },
        },
        {
          title: this.$t("comptabilite.description"),
          dataIndex: "description",
          key: "description",
          ellipsis: true,
        },
        {
          title: this.$t("action.actions"),
          key: "action",
          fixed: "right",
          scopedSlots: { customRender: "action" },
        },
      ];
    },
  },
  created() {
    this.load();
  },
  methods: {
    kindLabel(v) {
      if (v === "CAPEX") return this.$t("comptabilite.expenseKindCapex");
      if (v === "OTHER") return this.$t("comptabilite.expenseKindOther");
      return this.$t("comptabilite.expenseKindOpex");
    },
    kindColor(v) {
      if (v === "CAPEX") return "purple";
      // "default" is too low contrast on white tables
      if (v === "OTHER") return "orange";
      return "blue";
    },
    onKindChange(v) {
      this.showCapexFormula = v === "CAPEX";
    },
    async load() {
      this.loading = true;
      try {
        const res = await apiClient.get("/compV3/depense/types");
        this.rows = res.data || [];
      } catch (e) {
        this.$message.error(this.$t("warning.connectionProblem"));
      } finally {
        this.loading = false;
      }
    },
    openModal(record) {
      this.editingId = record ? record._id : null;
      this.modalVisible = true;
      this.$nextTick(() => {
        if (record) {
          const ek = record.expenseKind || "OPEX";
          this.showCapexFormula = ek === "CAPEX";
          this.form.setFieldsValue({
            name: record.name,
            expenseKind: ek,
            capexFormula: record.capexFormula || "",
            description: record.description || "",
          });
        } else {
          this.showCapexFormula = false;
          this.form.resetFields();
          this.form.setFieldsValue({
            expenseKind: "OPEX",
            capexFormula: "",
            description: "",
          });
        }
      });
    },
    closeModal() {
      this.modalVisible = false;
      this.editingId = null;
    },
    onModalOk(e) {
      e.preventDefault();
      this.submit();
    },
    submit() {
      this.form.validateFields((err, values) => {
        if (err) return;
        if (values.expenseKind !== "CAPEX") values.capexFormula = "";
        this.saving = true;
        const payload = { data: values };
        const req = this.editingId
          ? apiClient.patch(`/compV3/depense/types/${this.editingId}`, payload)
          : apiClient.put("/compV3/depense/types", payload);
        req
          .then((res) => {
            const cascade = res.data && res.data.cascade;
            if (
              cascade &&
              typeof cascade.categoriesDeactivated === "number"
            ) {
              this.$message.success(
                this.$t("comptabilite.depenseTypeDeactivatedCascade", {
                  categories: cascade.categoriesDeactivated,
                  subcategories: cascade.subcategoriesDeactivated,
                })
              );
            } else {
              this.$message.success(this.$t("success.operationEffectue"));
            }
            this.closeModal();
            this.load();
          })
          .catch((err) => showApiError(this, err))
          .finally(() => {
            this.saving = false;
          });
      });
    },
    remove(id) {
      apiClient
        .patch(`/compV3/depense/types/${id}`, {
          data: { status: "inactive" },
        })
        .then((res) => {
          const cascade = res.data && res.data.cascade;
          if (
            cascade &&
            typeof cascade.categoriesDeactivated === "number"
          ) {
            this.$message.success(
              this.$t("comptabilite.depenseTypeDeactivatedCascade", {
                categories: cascade.categoriesDeactivated,
                subcategories: cascade.subcategoriesDeactivated,
              })
            );
          } else {
            this.$message.success(this.$t("success.operationEffectue"));
          }
          this.load();
        })
        .catch((err) => showApiError(this, err));
    },
  },
};
</script>
<style scoped>
.compact-param-form >>> .ant-form-item {
  margin-bottom: 10px;
}
</style>
